import { cn } from "@/lib/utils";
import { ImageOffIcon } from "lucide-react";
import { useEffect, useState, type ComponentProps } from "react";
import { useTenantLogoBlob } from "@/modules/tenants/tenants.api";
import Skeleton from "@/lib/components/ui/skeleton";

export type TenantLogoProps = ComponentProps<"img"> & {
  logoUrl?: string;
};

export function TenantLogo({ logoUrl, className, ...props }: TenantLogoProps) {
  const trimmed = logoUrl?.trim() ?? "";

  const { error, isLoading, isRefetching, tenantLogoBlob } =
    useTenantLogoBlob(logoUrl);

  const [objectSrc, setObjectSrc] = useState<string | undefined>();
  const isObjectUrlPending =
    Boolean(tenantLogoBlob?.size) && objectSrc === undefined;

  useEffect(
    function syncTenantLogoObjectUrl() {
      if (!tenantLogoBlob?.size) {
        // eslint-disable-next-line react-hooks/set-state-in-effect -- clear revoked preview when blob is gone
        setObjectSrc(undefined);
        return;
      }

      const url = URL.createObjectURL(tenantLogoBlob);
      setObjectSrc(url);
      return function revokeTenantLogoObjectUrl() {
        URL.revokeObjectURL(url);
      };
    },
    [tenantLogoBlob],
  );

  if (!trimmed) {
    return null;
  }

  if (isLoading || isRefetching || isObjectUrlPending) {
    return <Skeleton className={className} aria-label="Loading tenant logo" />;
  }

  if (error) {
    return (
      <div
        role="img"
        className={cn("flex shrink-0 items-center justify-center", className)}
        aria-label="Company logo could not be loaded"
      >
        <ImageOffIcon className="text-foreground-critical" aria-hidden />
      </div>
    );
  }

  if (!objectSrc) {
    return (
      <div
        role="img"
        className={cn("flex shrink-0 items-center justify-center", className)}
        aria-label="Company logo not found"
      >
        <ImageOffIcon className="text-foreground" aria-hidden />
      </div>
    );
  }

  return (
    <img
      src={objectSrc}
      className={cn("object-contain", className)}
      {...props}
    />
  );
}
